import { z } from "zod";

export const OpaqueIdSchema = z
  .string()
  .min(5)
  .max(128)
  .regex(/^[a-z][a-z0-9_]*$/, "Expected an opaque platform ID");

export const UsernameSchema = z
  .string()
  .trim()
  .min(3)
  .max(64)
  .regex(
    /^[a-zA-Z0-9._@-]+$/,
    "Use letters, numbers, dots, dashes, underscores, or an email address",
  )
  .transform((value) => value.toLowerCase());

export const PasswordSchema = z
  .string()
  .min(8)
  .max(1024)
  .refine(
    (password) => new TextEncoder().encode(password).byteLength <= 1024,
    "Password is too long",
  );

export const LoginRequestSchema = z.strictObject({
  username: UsernameSchema,
  password: PasswordSchema,
});

export const PlatformRoleSchema = z.enum(["platform_admin", "organization_admin", "member"]);

export const WorkspacePrimaryColorSchema = z
  .string()
  .trim()
  .toLowerCase()
  .regex(/^#[0-9a-f]{6}$/, "Enter a six-digit color such as #b42318.");

export const UserSummarySchema = z.strictObject({
  id: OpaqueIdSchema,
  username: UsernameSchema,
  displayName: z.string().min(1).max(120),
});

export const OrganizationSummarySchema = z.strictObject({
  id: OpaqueIdSchema,
  slug: z
    .string()
    .min(2)
    .max(64)
    .regex(/^[a-z0-9-]+$/),
  name: z.string().min(1).max(160),
  role: PlatformRoleSchema,
  logoVersion: z.number().int().positive().nullable(),
  faviconVersion: z.number().int().positive().nullable(),
  primaryColor: WorkspacePrimaryColorSchema.nullable(),
});

export const WorkspaceEntryBrandingResponseSchema = z.strictObject({
  workspace: z
    .strictObject({
      id: OpaqueIdSchema,
      name: z.string().min(1).max(160),
      logoVersion: z.number().int().positive().nullable(),
      faviconVersion: z.number().int().positive().nullable(),
      primaryColor: WorkspacePrimaryColorSchema.nullable(),
    })
    .nullable(),
});

const RESERVED_WORKSPACE_SUBDOMAINS = new Set([
  "admin",
  "api",
  "app",
  "assets",
  "login",
  "status",
  "support",
  "www",
]);

export const WorkspaceSubdomainSchema = z
  .string()
  .trim()
  .toLowerCase()
  .min(2, "Enter at least 2 characters.")
  .max(63, "Keep the subdomain under 64 characters.")
  .regex(/^[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?$/, "Use lowercase letters, numbers, or dashes.")
  .refine(
    (value) => !value.startsWith("i-"),
    "Subdomains beginning with “i-” are reserved for CCBox tools.",
  )
  .refine(
    (value) => !RESERVED_WORKSPACE_SUBDOMAINS.has(value),
    "That subdomain is reserved by CCBox.",
  );

export const ShellOriginSchema = z
  .url()
  .max(253)
  .refine((value) => {
    const url = new URL(value);
    return (
      url.protocol === "https:" &&
      url.port === "" &&
      url.username === "" &&
      url.password === "" &&
      url.pathname === "/" &&
      url.search === "" &&
      url.hash === "" &&
      (url.hostname === "app.ccbox.ai" ||
        (url.hostname.endsWith(".ccbox.ai") &&
          WorkspaceSubdomainSchema.safeParse(url.hostname.slice(0, -".ccbox.ai".length)).success))
    );
  }, "Expected a CCBox shell origin");

export const WorkspaceShellPathSchema = z
  .string()
  .min(1)
  .max(2048)
  .refine(
    (value) =>
      value.startsWith("/") &&
      !value.startsWith("//") &&
      !value.includes("\\") &&
      Array.from(value).every((character) => {
        const codeUnit = character.charCodeAt(0);
        return codeUnit >= 32 && codeUnit !== 127;
      }),
    "Expected a relative CCBox shell path",
  );

export const WorkspaceTransitionRequestSchema = z.strictObject({
  organizationId: OpaqueIdSchema,
  intendedPath: WorkspaceShellPathSchema.default("/"),
});

export const WorkspaceTransitionExchangeSchema = z.strictObject({
  grant: z.string().min(32).max(256),
});

export const WorkspaceTransitionCreatedSchema = z.strictObject({
  targetOrigin: ShellOriginSchema,
  expiresAt: z.number().int().positive(),
});

export const AppIconSchema = z.enum(["quotes", "parts", "files", "listener", "table", "tool"]);
export const AppColorSchema = z.string().regex(/^#[0-9a-f]{6}$/i, "Expected a six-digit hex color");

export const AppSummarySchema = z.strictObject({
  appInstanceId: OpaqueIdSchema,
  displayName: z.string().min(1).max(120),
  description: z.string().min(1).max(320),
  icon: AppIconSchema,
  color: AppColorSchema,
  pinned: z.boolean(),
  lastOpenedAt: z.number().int().positive().nullable(),
  technicalOrigin: z.url().refine((url) => url.startsWith("https://i-"), {
    message: "Expected an isolated CCBox instance origin",
  }),
});

export const MeResponseSchema = z.strictObject({
  user: UserSummarySchema,
  organizations: z.array(OrganizationSummarySchema).max(100),
});

export const AppsResponseSchema = z.strictObject({
  organization: OrganizationSummarySchema,
  apps: z.array(AppSummarySchema).max(100),
});

export const WorkspaceMemberSchema = z.strictObject({
  membershipId: OpaqueIdSchema,
  user: UserSummarySchema,
  role: PlatformRoleSchema,
  toolAccess: z.array(OpaqueIdSchema).max(100),
});

export const WorkspaceMemberRoleSchema = z.enum(["organization_admin", "member"]);

const WorkspaceToolAccessSelectionSchema = z
  .array(OpaqueIdSchema)
  .max(100)
  .refine(
    (toolAccess) => new Set(toolAccess).size === toolAccess.length,
    "Choose each tool only once.",
  );

export const WorkspaceMemberCreateSchema = z
  .strictObject({
    accountType: z.enum(["new", "existing"]),
    username: UsernameSchema,
    displayName: z
      .string()
      .trim()
      .min(2, "Enter the person’s name.")
      .max(120, "Keep the name under 120 characters.")
      .nullable(),
    temporaryPassword: PasswordSchema.nullable(),
    role: WorkspaceMemberRoleSchema,
    toolAccess: WorkspaceToolAccessSelectionSchema,
  })
  .superRefine((input, context) => {
    if (input.accountType === "new") {
      if (!input.displayName) {
        context.addIssue({
          code: "custom",
          message: "Enter the person’s name.",
          path: ["displayName"],
        });
      }
      if (!input.temporaryPassword) {
        context.addIssue({
          code: "custom",
          message: "Enter a temporary password with at least 8 characters.",
          path: ["temporaryPassword"],
        });
      }
    } else if (input.displayName || input.temporaryPassword) {
      context.addIssue({
        code: "custom",
        message: "Existing accounts keep their current name and password.",
        path: ["accountType"],
      });
    }
    if (input.role === "organization_admin" && input.toolAccess.length > 0) {
      context.addIssue({
        code: "custom",
        message: "Workspace administrators automatically receive every tool.",
        path: ["toolAccess"],
      });
    }
  });

export const WorkspaceMemberAccessUpdateSchema = z.strictObject({
  toolAccess: WorkspaceToolAccessSelectionSchema,
});

export const WorkspaceToolSchema = AppSummarySchema.omit({
  technicalOrigin: true,
  pinned: true,
  lastOpenedAt: true,
});

export const AppPreferenceUpdateSchema = z.strictObject({
  pinned: z.boolean(),
});

export const AppPreferenceResultSchema = z.strictObject({
  appInstanceId: OpaqueIdSchema,
  pinned: z.boolean(),
  updatedAt: z.number().int().positive(),
});

export const WorkspaceAdminResponseSchema = z.strictObject({
  organization: OrganizationSummarySchema,
  members: z.array(WorkspaceMemberSchema).max(500),
  tools: z.array(WorkspaceToolSchema).max(100),
});

export const WorkspaceMemberCreatedSchema = z.strictObject({
  organizationId: OpaqueIdSchema,
  member: WorkspaceMemberSchema,
  accountCreated: z.boolean(),
  updatedAt: z.number().int().positive(),
});

export const WorkspaceMemberAccessResultSchema = z.strictObject({
  organizationId: OpaqueIdSchema,
  membershipId: OpaqueIdSchema,
  toolAccess: z.array(OpaqueIdSchema).max(100),
  updatedAt: z.number().int().positive(),
});

export const WorkspaceSettingsUpdateSchema = z.strictObject({
  name: z
    .string()
    .trim()
    .min(2, "Enter a workspace name with at least 2 characters.")
    .max(160, "Keep the workspace name under 160 characters."),
});

export const WorkspaceAppearanceUpdateSchema = z.strictObject({
  primaryColor: WorkspacePrimaryColorSchema.nullable(),
});

export const WorkspaceSubdomainUpdateSchema = z.strictObject({
  subdomain: WorkspaceSubdomainSchema,
});

export const WorkspaceToolUpdateSchema = z.strictObject({
  displayName: z
    .string()
    .trim()
    .min(2, "Enter a tool name with at least 2 characters.")
    .max(120, "Keep the tool name under 120 characters."),
  description: z
    .string()
    .trim()
    .min(10, "Describe the tool in at least 10 characters.")
    .max(320, "Keep the description under 320 characters."),
  icon: AppIconSchema,
});

export const WorkspaceAccessUpdateSchema = z.strictObject({
  granted: z.boolean(),
});

export const WorkspaceMutationResultSchema = z.strictObject({
  organizationId: OpaqueIdSchema,
  updatedAt: z.number().int().positive(),
});

export const WorkspaceSubdomainResultSchema = WorkspaceMutationResultSchema.extend({
  subdomain: WorkspaceSubdomainSchema,
}).strict();

export const WorkspaceSubdomainViewSchema = z.strictObject({
  organizationId: OpaqueIdSchema,
  subdomain: WorkspaceSubdomainSchema.nullable(),
  canEdit: z.boolean(),
});

export const WorkspaceToolAccessResultSchema = WorkspaceMutationResultSchema.extend({
  appInstanceId: OpaqueIdSchema,
  membershipId: OpaqueIdSchema,
  granted: z.boolean(),
}).strict();

export const WorkspaceLogoResultSchema = WorkspaceMutationResultSchema.extend({
  logoVersion: z.number().int().positive().nullable(),
}).strict();

export const WorkspaceFaviconResultSchema = WorkspaceMutationResultSchema.extend({
  faviconVersion: z.number().int().positive().nullable(),
}).strict();

export const ToolRequestSubmissionSchema = z.strictObject({
  organizationId: OpaqueIdSchema,
  whatItDoes: z
    .string()
    .trim()
    .min(10, "Describe what the tool should do in at least 10 characters.")
    .max(4000, "Keep the tool description under 4,000 characters."),
  systems: z
    .string()
    .trim()
    .min(2, "Name the systems involved, or enter “None.”")
    .max(1000, "Keep the systems description under 1,000 characters."),
});

export const ToolRequestCreatedSchema = z.strictObject({
  id: OpaqueIdSchema,
  organizationId: OpaqueIdSchema,
  status: z.literal("submitted"),
  createdAt: z.number().int().positive(),
});

export const DeploymentHashSchema = z.string().regex(/^sha256:[a-f0-9]{64}$/);

export const PlatformHealthResponseSchema = z.strictObject({
  status: z.literal("ok"),
  service: z.literal("ccbox-platform"),
  environment: z.enum(["staging", "test"]),
  deploymentVersion: z.uuid(),
});

export const AppDetailResponseSchema = z.strictObject({
  organization: OrganizationSummarySchema,
  app: AppSummarySchema,
  deploymentHash: DeploymentHashSchema.nullable(),
});

export const RelativeAppPathSchema = z
  .string()
  .min(1)
  .max(2048)
  .startsWith("/")
  .refine((value) => !value.startsWith("//"), "Network paths are not allowed")
  .refine(
    (value) => new URL(value, "https://ccbox.invalid").origin === "https://ccbox.invalid",
    "Expected a relative application path",
  );

export const LaunchRequestSchema = z.strictObject({
  intendedPath: RelativeAppPathSchema.default("/"),
});

export const LaunchResponseSchema = z.strictObject({
  appInstanceId: OpaqueIdSchema,
  technicalOrigin: z.url(),
  grant: z.string().min(32).max(256),
  expiresAt: z.number().int().positive(),
});

export const SessionExchangeRequestSchema = z.strictObject({
  grant: z.string().min(32).max(256),
});

export const SessionExchangeResponseSchema = z.strictObject({
  intendedPath: RelativeAppPathSchema,
});

export const TrustedAppContextSchema = z.strictObject({
  requestId: OpaqueIdSchema,
  userId: OpaqueIdSchema,
  organizationId: OpaqueIdSchema,
  appInstanceId: OpaqueIdSchema,
  roles: z.array(PlatformRoleSchema).min(1).max(16),
});

export const BootstrapReadyMessageSchema = z.strictObject({
  type: z.literal("platform:bootstrap-ready"),
  appInstanceId: OpaqueIdSchema,
});

export const BootstrapProbeMessageSchema = z.strictObject({
  type: z.literal("platform:bootstrap-probe"),
  appInstanceId: OpaqueIdSchema,
});

export const LaunchGrantMessageSchema = z.strictObject({
  type: z.literal("platform:launch-grant"),
  appInstanceId: OpaqueIdSchema,
  grant: z.string().min(32).max(256),
});

export const AppSessionExpiredMessageSchema = z.strictObject({
  type: z.literal("platform:app-session-expired"),
});

export const AppMessageSchema = z.discriminatedUnion("type", [
  BootstrapReadyMessageSchema,
  AppSessionExpiredMessageSchema,
]);

export const ApiErrorSchema = z.strictObject({
  error: z.strictObject({
    code: z.string().min(1).max(64),
    message: z.string().min(1).max(240),
    requestId: OpaqueIdSchema.optional(),
  }),
});

export type LoginRequest = z.infer<typeof LoginRequestSchema>;
export type UserSummary = z.infer<typeof UserSummarySchema>;
export type OrganizationSummary = z.infer<typeof OrganizationSummarySchema>;
export type WorkspaceEntryBrandingResponse = z.infer<typeof WorkspaceEntryBrandingResponseSchema>;
export type AppSummary = z.infer<typeof AppSummarySchema>;
export type AppPreferenceUpdate = z.infer<typeof AppPreferenceUpdateSchema>;
export type AppPreferenceResult = z.infer<typeof AppPreferenceResultSchema>;
export type MeResponse = z.infer<typeof MeResponseSchema>;
export type AppsResponse = z.infer<typeof AppsResponseSchema>;
export type WorkspaceAdminResponse = z.infer<typeof WorkspaceAdminResponseSchema>;
export type WorkspaceMemberCreate = z.infer<typeof WorkspaceMemberCreateSchema>;
export type WorkspaceMemberCreated = z.infer<typeof WorkspaceMemberCreatedSchema>;
export type WorkspaceMemberAccessUpdate = z.infer<typeof WorkspaceMemberAccessUpdateSchema>;
export type WorkspaceMemberAccessResult = z.infer<typeof WorkspaceMemberAccessResultSchema>;
export type WorkspaceSettingsUpdate = z.infer<typeof WorkspaceSettingsUpdateSchema>;
export type WorkspaceAppearanceUpdate = z.infer<typeof WorkspaceAppearanceUpdateSchema>;
export type WorkspaceSubdomainUpdate = z.infer<typeof WorkspaceSubdomainUpdateSchema>;
export type WorkspaceToolUpdate = z.infer<typeof WorkspaceToolUpdateSchema>;
export type WorkspaceAccessUpdate = z.infer<typeof WorkspaceAccessUpdateSchema>;
export type WorkspaceMutationResult = z.infer<typeof WorkspaceMutationResultSchema>;
export type WorkspaceSubdomainResult = z.infer<typeof WorkspaceSubdomainResultSchema>;
export type WorkspaceSubdomainView = z.infer<typeof WorkspaceSubdomainViewSchema>;
export type WorkspaceTransitionRequest = z.infer<typeof WorkspaceTransitionRequestSchema>;
export type WorkspaceTransitionCreated = z.infer<typeof WorkspaceTransitionCreatedSchema>;
export type WorkspaceToolAccessResult = z.infer<typeof WorkspaceToolAccessResultSchema>;
export type WorkspaceLogoResult = z.infer<typeof WorkspaceLogoResultSchema>;
export type WorkspaceFaviconResult = z.infer<typeof WorkspaceFaviconResultSchema>;
export type ToolRequestSubmission = z.infer<typeof ToolRequestSubmissionSchema>;
export type ToolRequestCreated = z.infer<typeof ToolRequestCreatedSchema>;
export type AppDetailResponse = z.infer<typeof AppDetailResponseSchema>;
export type PlatformHealthResponse = z.infer<typeof PlatformHealthResponseSchema>;
export type LaunchRequest = z.infer<typeof LaunchRequestSchema>;
export type LaunchResponse = z.infer<typeof LaunchResponseSchema>;
export type TrustedAppContext = z.infer<typeof TrustedAppContextSchema>;
export type AppMessage = z.infer<typeof AppMessageSchema>;

export * from "./desktop";
